import React, { useState, useEffect } from "react";
import { X, Truck, Package, CalendarCheck, MapPin } from "lucide-react";
import { trackInvoice } from "../../services/invoiceService";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return "-";
  const date = new Date(dateTimeStr);
  return (
    date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }) +
    " " +
    date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })
  );
};

const TrackingView = ({ chaveNfe, onClose }) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        setLoading(true);
        const data = await trackInvoice(chaveNfe);
        setTracking(data);
      } catch (err) {
        setError("Erro ao carregar informações de rastreamento");
        console.error("Erro:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
  }, [chaveNfe]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-sm">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 mr-3"></div>
            <p className="text-gray-600">
              Carregando informações de rastreamento...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tracking || !tracking.success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-700">
              Rastreamento
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="text-center">
            <div className="text-red-600 font-medium mb-2">
              Documento não encontrado!
            </div>
            <p className="text-gray-700">
              Por favor entre em contato com a Central.
            </p>
          </div>
          <button
            onClick={onClose}
            className="mt-4 w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  const { documento } = tracking;
  const { header, tracking: trackingEvents } = documento;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-6 mx-auto w-full max-w-4xl mb-6 px-4">
        <div className="bg-white shadow-lg rounded-lg">
          {/* Cabeçalho */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center">
              <Truck className="w-6 h-6 mr-2 text-red-600" />
              <h2 className="text-xl font-semibold text-gray-700">
                Rastreamento da Entrega
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6">
            {/* Informações da Nota */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Informações da Entrega
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Nota Fiscal
                    </label>
                    <p className="mt-1">{header.nro_nf.padStart(9,0)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Remetente
                    </label>
                    <p className="mt-1">{header.remetente}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Destinatário
                    </label>
                    <p className="mt-1">{header.destinatario}</p>
                  </div>
                  {header.pedido && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        Pedido
                      </label>
                      <p className="mt-1">{header.pedido}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Timeline de Eventos */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Histórico de Rastreamento
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-6">
                  {trackingEvents.map((event, index) => (
                    <div
                      key={index}
                      className={`flex ${
                        index !== trackingEvents.length - 1
                          ? "pb-6 border-b border-gray-200"
                          : ""
                      }`}
                    >
                      <div className="flex-shrink-0 mr-4">
                        {event.tipo === "Entrega" ? (
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <CalendarCheck className="w-6 h-6 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Package className="w-6 h-6 text-blue-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-grow">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-1">
                          <h4 className="text-base font-medium text-gray-900">
                            {event.ocorrencia}
                          </h4>
                          <span className="text-sm text-gray-500">
                            {formatDateTime(event.data_hora)}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <MapPin className="w-4 h-4 mr-1" />
                          {event.cidade}
                        </div>
                        <p className="text-sm text-gray-700">
                          {event.descricao}
                        </p>
                        {event.nome_recebedor && (
                          <p className="text-sm text-gray-700 mt-1">
                            <span className="font-medium">Recebido por:</span>{" "}
                            {event.nome_recebedor}
                            {event.nro_doc_recebedor &&
                              ` (${event.nro_doc_recebedor})`}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingView;
